import i18n from 'i18next';
import { initReactI18next } from 'react-i18next';
import LanguageDetector from 'i18next-browser-languagedetector';

import enAssignTraining from './locales/en/volunteering/assignTraining.json';
import enCreateTeam from './locales/en/volunteering/createTeam.json';
import enEditMemberRoles from './locales/en/volunteering/editMemberRoles.json';
import enEditTeam from './locales/en/volunteering/editTeam.json';
import enInviteVolunteer from './locales/en/volunteering/inviteVolunteer.json';
import enLessonEditor from './locales/en/volunteering/lessonEditor.json';
import enManageRoles from './locales/en/volunteering/manageRoles.json';
import enModuleEditor from './locales/en/volunteering/moduleEditor.json';
import enRemoveVolunteer from './locales/en/volunteering/removeVolunteer.json';
import enTraining from './locales/en/volunteering/training.json';
import enTrainingModule from './locales/en/volunteering/trainingModule.json';
import enVolunteer from './locales/en/volunteering/volunteer.json';
import enVolunteering from './locales/en/volunteering/volunteering.json';

import frAssignTraining from './locales/fr/volunteering/assignTraining.json';
import frCreateTeam from './locales/fr/volunteering/createTeam.json';
import frEditMemberRoles from './locales/fr/volunteering/editMemberRoles.json';
import frEditTeam from './locales/fr/volunteering/editTeam.json';
import frInviteVolunteer from './locales/fr/volunteering/inviteVolunteer.json';
import frLessonEditor from './locales/fr/volunteering/lessonEditor.json';
import frManageRoles from './locales/fr/volunteering/manageRoles.json';
import frModuleEditor from './locales/fr/volunteering/moduleEditor.json';
import frRemoveVolunteer from './locales/fr/volunteering/removeVolunteer.json';
import frTraining from './locales/fr/volunteering/training.json';
import frTrainingModule from './locales/fr/volunteering/trainingModule.json';
import frVolunteer from './locales/fr/volunteering/volunteer.json';
import frVolunteering from './locales/fr/volunteering/volunteering.json';

import frCAInviteVolunteer from './locales/fr-CA/volunteering/inviteVolunteer.json';

// Set up like Launchpad-Web's, so strings can move between the two: one file
// per namespace, so each screen's copy lives in one obvious place to review.
// Files are grouped by feature (src/i18n/locales/<lang>/<feature>/<ns>.json);
// the namespace is the file name, so it has to be unique across features.
//
// `fr` is the base French catalog and carries every namespace. `fr-CA` is a
// thin regional overlay: it ships only files whose wording genuinely differs
// in Canadian French ("courriel" over "e-mail", for one), and anything it
// omits falls through to `fr`, then to `en`.
//
// With nonExplicitSupportedLngs, regional variants that aren't shipped
// explicitly (fr-FR, fr-BE, fr-CH...) resolve to `fr`, so every French
// speaker gets French rather than English.
//
// Only Volunteering is translated so far; the rest of the Church Hub still
// renders its strings inline, in English.
export const resources = {
  en: {
    assignTraining: enAssignTraining,
    createTeam: enCreateTeam,
    editMemberRoles: enEditMemberRoles,
    editTeam: enEditTeam,
    inviteVolunteer: enInviteVolunteer,
    lessonEditor: enLessonEditor,
    manageRoles: enManageRoles,
    moduleEditor: enModuleEditor,
    removeVolunteer: enRemoveVolunteer,
    training: enTraining,
    trainingModule: enTrainingModule,
    volunteer: enVolunteer,
    volunteering: enVolunteering
  },
  fr: {
    assignTraining: frAssignTraining,
    createTeam: frCreateTeam,
    editMemberRoles: frEditMemberRoles,
    editTeam: frEditTeam,
    inviteVolunteer: frInviteVolunteer,
    lessonEditor: frLessonEditor,
    manageRoles: frManageRoles,
    moduleEditor: frModuleEditor,
    removeVolunteer: frRemoveVolunteer,
    training: frTraining,
    trainingModule: frTrainingModule,
    volunteer: frVolunteer,
    volunteering: frVolunteering
  },
  'fr-CA': {
    inviteVolunteer: frCAInviteVolunteer
  }
} as const;

export const SUPPORTED_LANGUAGES = ['en', 'fr', 'fr-CA'] as const;
export const NAMESPACES = [
  'assignTraining',
  'createTeam',
  'editMemberRoles',
  'editTeam',
  'inviteVolunteer',
  'lessonEditor',
  'manageRoles',
  'moduleEditor',
  'removeVolunteer',
  'training',
  'trainingModule',
  'volunteer',
  'volunteering'
] as const;

i18n
  .use(LanguageDetector)
  .use(initReactI18next)
  .init({
    resources,
    ns: NAMESPACES as unknown as string[],
    defaultNS: 'volunteering',
    supportedLngs: SUPPORTED_LANGUAGES as unknown as string[],
    nonExplicitSupportedLngs: true,
    fallbackLng: {
      'fr-CA': ['fr', 'en'],
      default: ['en']
    },
    // fr-CA ships only the namespaces it overrides, so a missing key must fall
    // through to fr rather than resolving to an empty string.
    fallbackNS: false,
    returnEmptyString: false,
    // See i18next.d.ts.
    returnNull: false,
    interpolation: {
      // React already escapes rendered values.
      escapeValue: false
    },
    detection: {
      // ?lang=fr is handy for QA and for reviewing a translation.
      order: ['querystring', 'localStorage', 'navigator'],
      lookupQuerystring: 'lang',
      lookupLocalStorage: 'lang',
      caches: ['localStorage']
    }
  });

// Keep <html lang> in step with the active language for screen readers,
// browser translation prompts, and correct hyphenation.
function syncDocumentLang(lng: string) {
  document.documentElement.lang = lng;
}

syncDocumentLang(i18n.resolvedLanguage ?? 'en');
i18n.on('languageChanged', syncDocumentLang);

export default i18n;
